"use client";

import * as React from "react";
import { MessageCircle, Paperclip, Calendar } from "lucide-react";
import { format, isPast, isToday, isTomorrow } from "date-fns";
import { cn } from "../../../lib/utils";
import { kanbanTagVariants } from "./constants";
import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import type {
  KanbanCardFooterProps,
  KanbanCardAssigneesProps,
  KanbanCardStatsProps,
  KanbanCardTagsProps,
  KanbanCardDueDateProps,
} from "./types";

// --- Footer Container ---

const KanbanCardFooter = React.forwardRef<
  HTMLDivElement,
  KanbanCardFooterProps
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "mt-2 flex items-center justify-between gap-2 pt-1.5",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));

KanbanCardFooter.displayName = "KanbanCardFooter";

// --- Assignees ---

function getInitials(name: string): string {
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const KanbanCardAssignees = React.forwardRef<
  HTMLDivElement,
  KanbanCardAssigneesProps
>(({ className, assignees = [], max = 3, ...props }, ref) => {
  if (assignees.length === 0) return null;

  const visible = assignees.slice(0, max);
  const remaining = assignees.length - max;

  return (
    <div
      ref={ref}
      className={cn("flex items-center -space-x-2", className)}
      {...props}
    >
      {visible.map((assignee) => (
        <Avatar key={assignee.id} className="size-5" title={assignee.name}>
          {assignee.avatar && (
            <AvatarImage src={assignee.avatar} alt={assignee.name} />
          )}
          <AvatarFallback className="text-xs font-medium">
            {getInitials(assignee.name)}
          </AvatarFallback>
        </Avatar>
      ))}
      {remaining > 0 && (
        <div className="flex size-5 items-center justify-center rounded-full bg-hover-strong">
          <span className="text-xs font-medium text-text-tertiary">
            +{remaining}
          </span>
        </div>
      )}
    </div>
  );
});

KanbanCardAssignees.displayName = "KanbanCardAssignees";

// --- Stats (Comments + Attachments) ---

const KanbanCardStats = React.forwardRef<HTMLDivElement, KanbanCardStatsProps>(
  ({ className, comments, attachments, ...props }, ref) => {
    const hasComments = comments !== undefined && comments > 0;
    const hasAttachments = attachments !== undefined && attachments > 0;

    if (!hasComments && !hasAttachments) return null;

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-3 text-xs text-text-tertiary",
          className,
        )}
        {...props}
      >
        {hasComments && (
          <div className="flex items-center gap-1">
            <MessageCircle className="size-3" aria-hidden="true" />
            <span>{comments}</span>
          </div>
        )}
        {hasAttachments && (
          <div className="flex items-center gap-1">
            <Paperclip className="size-3" aria-hidden="true" />
            <span>{attachments}</span>
          </div>
        )}
      </div>
    );
  },
);

KanbanCardStats.displayName = "KanbanCardStats";

// --- Tags ---

const KanbanCardTags = React.forwardRef<HTMLDivElement, KanbanCardTagsProps>(
  ({ className, tags = [], max = 3, ...props }, ref) => {
    if (tags.length === 0) return null;

    const visible = tags.slice(0, max);
    const remaining = tags.length - max;

    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-1 flex-wrap", className)}
        {...props}
      >
        {visible.map((tag, i) => (
          <span
            key={`${tag.label}-${i}`}
            className={cn(kanbanTagVariants({ color: tag.color || "default" }))}
          >
            {tag.label}
          </span>
        ))}
        {remaining > 0 && (
          <span className={cn(kanbanTagVariants({ color: "default" }))}>
            +{remaining}
          </span>
        )}
      </div>
    );
  },
);

KanbanCardTags.displayName = "KanbanCardTags";

// --- Due Date ---

const KanbanCardDueDate = React.forwardRef<
  HTMLDivElement,
  KanbanCardDueDateProps
>(({ className, date, ...props }, ref) => {
  if (!date) return null;

  const dateObj = typeof date === "string" ? new Date(date) : date;

  let displayText: string;
  let colorClass = "text-text-tertiary";

  if (isToday(dateObj)) {
    displayText = "Today";
    colorClass = "text-warning";
  } else if (isTomorrow(dateObj)) {
    displayText = "Tomorrow";
    colorClass = "text-warning";
  } else if (isPast(dateObj)) {
    displayText = format(dateObj, "MMM d");
    colorClass = "text-danger";
  } else {
    displayText = format(dateObj, "MMM d");
  }

  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-1 text-xs", colorClass, className)}
      {...props}
    >
      <Calendar className="size-3" aria-hidden="true" />
      <span>{displayText}</span>
    </div>
  );
});

KanbanCardDueDate.displayName = "KanbanCardDueDate";

export {
  KanbanCardFooter,
  KanbanCardAssignees,
  KanbanCardStats,
  KanbanCardTags,
  KanbanCardDueDate,
};
